import { useCallback, useMemo, useReducer } from 'react';
import { Nullable } from '@aibees/shared/definition';

export type PopoverState = {
  open: HTMLElement | null;
  visiblity: boolean;
  loading: boolean;
};

type PopoverAction =
  | { type: 'OPEN'; element: HTMLElement }
  | { type: 'CLOSE' }
  | { type: 'TOGGLE_VISIBILITY' }
  | { type: 'SET_LOADING'; loading: boolean };

function popoverReducer(
  state: PopoverState,
  action: PopoverAction
): PopoverState {
  switch (action.type) {
    case 'OPEN':
      return { ...state, open: action.element, visiblity: true };
    case 'CLOSE':
      return { ...state, open: null, loading: false };
    case 'TOGGLE_VISIBILITY':
      return { ...state, visiblity: !state.visiblity };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    default:
      return state;
  }
}

export type PopoverType = {
  open: HTMLElement | null;
  visiblity: boolean;
  loading: boolean;
  onOpen: (element: Nullable<HTMLElement>) => void;
  onClose: () => void;
  toggleVisiblity: () => void;
  showLoading: () => void;
  hideLoading: () => void;
};

export default function usePopover(initialVisibility = true): PopoverType {
  const [state, dispatch] = useReducer(popoverReducer, {
    open: null,
    visiblity: initialVisibility,
    loading: false,
  });

  const onOpen = useCallback((element: Nullable<HTMLElement>) => {
    if (element) dispatch({ type: 'OPEN', element });
  }, []);

  const onClose = useCallback(() => {
    dispatch({ type: 'CLOSE' });
  }, []);

  const toggleVisiblity = useCallback(() => {
    dispatch({ type: 'TOGGLE_VISIBILITY' });
  }, []);

  const showLoading = useCallback(() => {
    dispatch({ type: 'SET_LOADING', loading: true });
  }, []);

  const hideLoading = useCallback(() => {
    dispatch({ type: 'SET_LOADING', loading: false });
  }, []);

  // 🏆 Memoize the returned object for stability
  return useMemo(
    () => ({
      open: state.open,
      visiblity: state.visiblity,
      loading: state.loading,
      onOpen,
      onClose,
      toggleVisiblity,
      showLoading,
      hideLoading,
    }),
    [state, onOpen, onClose, toggleVisiblity, showLoading, hideLoading]
  );
}
